import * as Yup from 'yup';

import { CONSTANT } from '../utils/constant';
import { isSequentialOrRepeatable } from '../utils/index';
import EN from '../../EN.json';
import FR from '../../FR.json';

const locale = localStorage.getItem('locale');
const {
  USERS: {
    USER_AUTHENTICATION: {
      REGISTRATION_SCHEMA_MESSAGES: {
        FIRST_NAME_REQUIRED,
        LAST_LAST_REQUIRED,
        INVALID_EMAIL,
        EMAIL_REQUIRED,
        PASSWORD_REQUIRED,
        PASSWORD_MATCHES,
        PASSWORD_REPEAT,
        REQUIRED,
        ALPHANUMERIC,
        ONLY_DIGITS,
        MUST_EXACTLY_10,
        MAX_BE_DIGIT_10,
        MAX_7_CHARS,
        MIN_7_CHARS,
        MAX_5_DIGIT,
        MIN_5_DIGIT,
        MAX_4_DIGIT,
        MIN_4_DIGIT
      }
    }
  }
} = locale === 'FR' ? FR : EN;

const channelsForOuletXid = [
  CONSTANT.CHANNELS.BCX_CARE,
  CONSTANT.CHANNELS.HEALTH,
  CONSTANT.CHANNELS.CSD,
  CONSTANT.CHANNELS.NBD,
  CONSTANT.CHANNELS.ADT
];

const isSalesRepIdRequired = channel =>
  channel && channel.length > 0 && !channelsForOuletXid.includes(channel[0].id);

const isXidRequired = channel =>
  channel && channel.length > 0 && channelsForOuletXid.includes(channel[0].id);

const isOutletRequired = channel =>
  channel && channel.length > 0 && !channelsForOuletXid.includes(channel[0].id);

const isOutletCodeRequired = channel =>
  channel &&
  channel.length > 0 &&
  [CONSTANT.CHANNELS.CORP_STORE].includes(channel[0].id);

const isPartnerRequired = channel =>
  channel &&
  channel.length > 0 &&
  [CONSTANT.CHANNELS.TQ_PARTENAIRE].includes(channel[0].id);

const isDealershipForSalesManager = channel =>
  channel &&
  channel.length > 0 &&
  [CONSTANT.CHANNELS.DEALER].includes(channel[0].id);

const isDealershipForSalesSupport = channel =>
  channel &&
  channel.length > 0 &&
  channel.includes(CONSTANT.CHANNELS.DEALER.toString());

export const RegisterAsSalesRepAndManager = Yup.object().shape({
  firstName: Yup.string().required(FIRST_NAME_REQUIRED),
  lastName: Yup.string().required(LAST_LAST_REQUIRED),
  email: Yup.string()
    .email(INVALID_EMAIL)
    .required(EMAIL_REQUIRED),
  password: Yup.string()
    .required(PASSWORD_REQUIRED)
    .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/, PASSWORD_MATCHES)
    .test('password', PASSWORD_REPEAT, isSequentialOrRepeatable),
  province: Yup.string().required(REQUIRED),
  channel: Yup.array().required(REQUIRED),
  outlet: Yup.string().when('channel', {
    is: isOutletRequired,
    then: Yup.string()
      .required(REQUIRED)
      .matches(/^[a-zA-Z0-9]+$/, ALPHANUMERIC)
      .min(10, MUST_EXACTLY_10)
      .max(10, MAX_BE_DIGIT_10),
    otherwise: Yup.string()
  }),
  outletCode: Yup.string().when('channel', {
    is: isOutletCodeRequired,
    then: Yup.string()
      .required(REQUIRED)
      .matches(/^[0-9]+$/, ONLY_DIGITS)
      .min(5, MAX_5_DIGIT)
      .max(5, MIN_5_DIGIT),
    otherwise: Yup.string()
  }),
  contactNumber: Yup.string()
    .required(REQUIRED)
    .matches(/^[0-9]+$/, ONLY_DIGITS)
    .min(10, MUST_EXACTLY_10)
    .max(10, MAX_BE_DIGIT_10),
  partnerId: Yup.string().when('channel', {
    is: isPartnerRequired,
    then: Yup.string().required(REQUIRED),
    otherwise: Yup.string()
  }),
  xid: Yup.string().when('channel', {
    is: isXidRequired,
    then: Yup.string()
      .required(REQUIRED)
      .matches(/^[a-zA-Z0-9]+$/, ALPHANUMERIC)
      .min(7, MIN_7_CHARS)
      .max(7, MAX_7_CHARS),
    otherwise: Yup.string()
  }),
  salesManager: Yup.string().when('channel', {
    is: isDealershipForSalesManager,
    then: Yup.string(),
    otherwise: Yup.string().required(REQUIRED)
  }),
  salesManagerEmail: Yup.string().when('channel', {
    is: isDealershipForSalesManager,
    then: Yup.string(),
    otherwise: Yup.string()
      .required(REQUIRED)
      .email(INVALID_EMAIL)
  }),
  dealership: Yup.array().when('channel', {
    is: isDealershipForSalesManager,
    then: Yup.array().required(REQUIRED),
    otherwise: Yup.array()
  }),
  channelOrgCode: Yup.string().when('channel', {
    is: isDealershipForSalesManager,
    then: Yup.string()
      .required(REQUIRED)
      .matches(/^[0-9]+$/, ONLY_DIGITS)
      .min(5, MAX_5_DIGIT)
      .max(5, MIN_5_DIGIT),
    otherwise: Yup.string()
  }),
  salesRepId: Yup.string().when('channel', {
    is: isSalesRepIdRequired,
    then: Yup.string()
      .required(REQUIRED)
      .matches(/^[a-zA-Z0-9]+$/, ALPHANUMERIC)
      .min(4, MAX_4_DIGIT)
      .max(4, MIN_4_DIGIT),
    otherwise: Yup.string()
  })
});

export const RegisterAsSalesSupport = Yup.object().shape({
  firstName: Yup.string().required(FIRST_NAME_REQUIRED),
  lastName: Yup.string().required(LAST_LAST_REQUIRED),
  province: Yup.array()
    .of(Yup.string())
    .required(REQUIRED),
  channel: Yup.array()
    .of(Yup.string())
    .required(REQUIRED),
  email: Yup.string()
    .email(INVALID_EMAIL)
    .required(EMAIL_REQUIRED),
  password: Yup.string()
    .required(PASSWORD_REQUIRED)
    .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/, PASSWORD_MATCHES)
    .test('password', PASSWORD_REPEAT, isSequentialOrRepeatable),
  xid: Yup.string()
    .required(REQUIRED)
    .matches(/^[a-zA-Z0-9]+$/, ALPHANUMERIC)
    .min(7, MIN_7_CHARS)
    .max(7, MAX_7_CHARS),
  dealership: Yup.array().when('channel', {
    is: isDealershipForSalesSupport,
    then: Yup.array().required(REQUIRED),
    otherwise: Yup.array()
  }),
  channelOrgCode: Yup.string().when('channel', {
    is: isDealershipForSalesSupport,
    then: Yup.string()
      .required(REQUIRED)
      .matches(/^[0-9]+$/, ONLY_DIGITS)
      .min(5, MAX_5_DIGIT)
      .max(5, MIN_5_DIGIT),
    otherwise: Yup.string()
  }),
  outletCode: Yup.string().when('channel', {
    is: isOutletCodeRequired,
    then: Yup.string()
      .required(REQUIRED)
      .matches(/^[0-9]+$/, ONLY_DIGITS)
      .min(5, MAX_5_DIGIT)
      .max(5, MIN_5_DIGIT),
    otherwise: Yup.string()
  }),
  contactNumber: Yup.string()
    .required(REQUIRED)
    .matches(/^[0-9]+$/, ONLY_DIGITS)
    .min(10, MUST_EXACTLY_10)
    .max(10, MAX_BE_DIGIT_10)
});
